import { GuideLineProps } from "@/app/activity/components/guideline/activityGuideLine/type"
import { ACTIVITY_TIMELINE_AXIS_X_ADJUST } from "@/app/activity/components/timeline/activityTimeline/constants"
import { getXPositionByDate } from "@/app/activity/components/timeline/activityTimeline/utils"
import {
  createActivityCommonStore,
  createActivityStore,
  createSessionStore,
} from "@/app/activity/store"
import { format } from "date-fns"
import { useEffect, useMemo, useRef } from "react"

import "./activityGuideline.scss"
import { select } from "d3-selection"
import { D3DragEvent, drag } from "d3-drag"
import { useGetTimePerWidth } from "@/app/activity/components/timeline/activityTimeline/hooks"
import { HOUR_TO_SECOND_FORMAT } from "@/constants/regex"

export default function GuidLine({
  isSession,
  isVisible,
  isTimeVisible,
  isHandleVisible = false,
  onDrag,
  onDragEnd,
}: GuideLineProps) {
  const LEFT_LINE_X_LIMIT = 50
  const RIGHT_LINE_X_LIMIT = 56
  const RIGHT_TEXT_X_LIMIT = 5
  const RIGHT_TEXT_TRANSFORM = 49

  const leftHandleRef = useRef<SVGLineElement>(null)
  const rightHandleRef = useRef<SVGLineElement>(null)

  const { activity, setActivityGuideLine } = createActivityStore((state) => ({
    activity: state.tempActivity,
    setActivityGuideLine: state.setActivityGuideLine,
  }))

  const { temporarySession } = createSessionStore((state) => ({
    temporarySession: state.temporarySession,
  }))

  const {
    range,
    timelineWidth,
    remainRightArea = 0,
    translateX,
  } = createActivityCommonStore((state) => ({
    range: state.range,
    remainRightArea: state.remainingArea,
    timelineWidth: state.timelineWidth,
    magnification: state.magnification,
    translateX: state.translateX,
  }))

  const timePerWidth = useGetTimePerWidth({
    dateStart: range.dateStart,
    dateFinish: range.dateFinish,
    chartWidth: timelineWidth,
  })

  const { startX, endX, dateFinish, dateStart } = useMemo(() => {
    const { dateStart, dateFinish } = range
    const { dateStart: activityDateStart, dateFinish: activityDateFinish } = activity || {}

    if (temporarySession) {
      const { dateStart: sessionStartDate, dateFinish: sessionFinishDate } = temporarySession
      const startX =
        getXPositionByDate(new Date(sessionStartDate), dateStart as Date, dateFinish as Date) *
        timelineWidth
      const endX =
        getXPositionByDate(new Date(sessionFinishDate), dateStart as Date, dateFinish as Date) *
        timelineWidth

      return {
        startX,
        endX,
        dateStart: sessionStartDate,
        dateFinish: sessionFinishDate,
      }
    }

    if (!activityDateStart || !activityDateFinish) {
      return {
        startX: 0,
        endX: 0,
        dateStart,
        dateFinish,
      }
    }

    const startX =
      getXPositionByDate(new Date(activityDateStart), dateStart as Date, dateFinish as Date) *
      timelineWidth
    const endX =
      getXPositionByDate(new Date(activityDateFinish), dateStart as Date, dateFinish as Date) *
      timelineWidth

    return {
      startX,
      endX,
      dateStart: activityDateStart,
      dateFinish: activityDateFinish,
    }
  }, [activity, range, timelineWidth, temporarySession])

  const leftLineX = useMemo(() => {
    if (isSession) {
      return Math.max(0, startX + remainRightArea / 2)
    }

    return Math.max(0, startX + translateX)
  }, [startX, isSession, translateX, remainRightArea])

  const rightLineX = useMemo(() => {
    if (isSession) {
      return endX + remainRightArea / 2 + (activity?.width || 0)
    }

    return endX + translateX + (activity?.width || 0)
  }, [endX, activity, isSession, translateX, remainRightArea])

  const formatDate = (date?: Date | string) => {
    if (!date) {
      return ""
    }

    return format(new Date(date), HOUR_TO_SECOND_FORMAT)
  }

  const setDragEvent = () => {
    if (leftHandleRef.current && isHandleVisible) {
      let startXPos = 0
      select(leftHandleRef.current as Element).call(
        drag()
          .on("start", (event: D3DragEvent<Element, unknown, unknown>) => {
            startXPos = event.x
          })
          .on("drag", (event: D3DragEvent<Element, unknown, unknown>) => {
            const { x } = event
            const moveX = x - startXPos

            onDrag?.({
              direction: "left",
              moveX,
              startX,
              endX,
              isActivity: true,
            })
          })
          .on("end", () => {
            onDragEnd?.()
          }),
      )
    }

    if (rightHandleRef.current && isHandleVisible) {
      let startXPos = 0
      select(rightHandleRef.current as Element).call(
        drag()
          .on("start", (event: D3DragEvent<Element, unknown, unknown>) => {
            startXPos = event.x
          })
          .on("drag", (event: D3DragEvent<Element, unknown, unknown>) => {
            const { x } = event
            const moveX = x - startXPos

            onDrag?.({
              direction: "right",
              moveX,
              startX,
              endX,
              isActivity: true,
            })
          })
          .on("end", () => {
            onDragEnd?.()
          }),
      )
    }
  }

  const cancelDragEvent = () => {
    if (leftHandleRef.current) {
      select(leftHandleRef.current as Element).on(".drag", null)
    }
    if (rightHandleRef.current) {
      select(rightHandleRef.current as Element).on(".drag", null)
    }
  }

  useEffect(() => {
    setDragEvent()

    return () => {
      cancelDragEvent()
    }
  }, [timePerWidth, temporarySession, activity, startX, endX, isHandleVisible])

  useEffect(() => {
    setActivityGuideLine({
      startX,
      endX,
      startDate: dateStart as Date,
      endDate: dateFinish as Date,
    })
  }, [startX, endX, dateFinish, dateStart])

  return (
    isVisible && (
      <svg width="100%" height="100%" className="GuideLineCont">
        <g className="timeline-blind">
          <rect
            width={Math.max(0, leftLineX - 1)}
            height="100%"
            fill="rgba(147,147,150)"
            fillOpacity={0.75}
          />
          <rect
            transform={`translate(${rightLineX + 1}, 0)`}
            width={timelineWidth + remainRightArea - endX}
            height="100%"
            fill="rgba(147,147,150)"
            fillOpacity={0.75}
          />
        </g>

        <g className="timeline-handle-lines">
          <line
            x1={Math.max(0, leftLineX - 1)}
            x2={Math.max(0, leftLineX - 1)}
            y1="0"
            y2="100%"
            stroke="black"
            strokeWidth={1}
          />
          <line
            x1={rightLineX + 1}
            x2={rightLineX + 1}
            y1="0"
            y2="100%"
            stroke="black"
            strokeWidth={1}
          />
        </g>

        {isHandleVisible && (
          <g className="timeline-handle-draggable">
            <line
              x1={leftLineX}
              x2={leftLineX}
              y1="0"
              y2="100%"
              stroke="transparent"
              strokeWidth={10}
              ref={leftHandleRef}
              style={{
                cursor: "ew-resize",
              }}
            />
            <line
              x1={rightLineX + 1}
              x2={rightLineX + 1}
              y1="0"
              y2="100%"
              stroke="transparent"
              strokeWidth={10}
              ref={rightHandleRef}
              style={{
                cursor: "ew-resize",
              }}
            />
          </g>
        )}
        {isTimeVisible && (
          <g>
            <rect
              transform={`translate(${Math.max(LEFT_LINE_X_LIMIT, leftLineX)}, 0)`}
              width="52"
              height="16"
              x="-53"
              y="2"
              rx="3"
              ry="3"
              fill="black"
            />
            <text
              className="guideline-text"
              x={Math.max(0, leftLineX - LEFT_LINE_X_LIMIT)}
              dominantBaseline="middle"
              textAnchor="start"
              y="11"
              fill="white">
              {formatDate(dateStart)}
            </text>
            <rect
              transform={`translate(${Math.min(rightLineX + RIGHT_LINE_X_LIMIT, timelineWidth + remainRightArea / 2)}, 0)`}
              width="52"
              height="16"
              x="-55"
              y="2"
              rx="3"
              ry="3"
              fill="black"
            />
            <text
              className="guideline-text"
              x={Math.min(
                rightLineX + RIGHT_TEXT_TRANSFORM,
                timelineWidth - RIGHT_TEXT_X_LIMIT + remainRightArea / 2,
              )}
              dominantBaseline="middle"
              textAnchor="end"
              y="11"
              fill="white">
              {formatDate(dateFinish)}
            </text>
          </g>
        )}
      </svg>
    )
  )
}
